import { ChatMessage } from './types';

export function initSocketHandlers(comp: any) {
    const socket = comp.socket;

    socket.on("connect", () => {
        if (comp.sessionReplaced) return;
        comp.setLoadingProgress(70, '초기화 중...');
        const joinData: any = {
            nickname: comp.nickname,
            status: comp.myStatus,
            department: comp.myDepartment,
            realName: comp.myRealName,
            title: comp.myTitle,
            userId: comp.userId,
            role: comp.myRole,
            shape: comp.myShape,
            room: comp.currentRoom,
            chatRoom: comp.chatRoom,
        };
        // 자리로 돌아가기 요청 시 seat_spawn 플래그 전달
        if (sessionStorage.getItem('seatSpawn') === 'true') {
            joinData.seat_spawn = true;
            sessionStorage.removeItem('seatSpawn');
        }
        socket.emit("join", joinData);
    });

    socket.on("connect_error", (err: any) => {
        console.error("Socket connection error:", err);
    });

    // 세션 교체: 다른 탭에서 동일 계정으로 접속하여 이 탭이 대체됨
    socket.on("session_replaced", () => {
        comp.sessionReplaced = true;
        comp.isLoading = true;
        if (comp.syncInterval) { clearInterval(comp.syncInterval); comp.syncInterval = null; }
        if (comp.animFrameId) { cancelAnimationFrame(comp.animFrameId); comp.animFrameId = 0; }
        socket.removeAllListeners();
        socket.disconnect();
        comp.service.render();
    });

    // 초기화: 서버에서 sid와 전체 플레이어 목록 수신
    socket.on("init", (data: any) => {
        comp.mySid = data.sid;

        // 입장 시각 저장 (채팅 가시성 필터용)
        const players = data.players || {};
        const myData = players[comp.mySid];
        if (myData && myData.join_time) {
            comp.joinTime = myData.join_time;
        }

        for (const sid of Object.keys(players)) {
            if (sid === comp.mySid) {
                comp.playerX = players[sid].x;
                comp.playerY = players[sid].y;
                comp.playerColor = players[sid].color;
                comp.myStatus = players[sid].status || 'in';
                comp.myShape = players[sid].shape || 'circle';
                comp.lastSyncX = comp.playerX;
                comp.lastSyncY = comp.playerY;
            } else {
                const pData = {
                    sid: sid,
                    userId: players[sid].userId || '',
                    nickname: players[sid].nickname,
                    x: players[sid].x,
                    y: players[sid].y,
                    targetX: players[sid].x,
                    targetY: players[sid].y,
                    color: players[sid].color,
                    status: players[sid].status || 'in',
                    department: players[sid].department || '',
                    realName: players[sid].realName || '',
                    title: players[sid].title || '',
                    shape: players[sid].shape || 'circle',
                    room: players[sid].room || '',
                };
                // 전체 접속자 목록에 추가
                comp.allRemotePlayers[sid] = pData;
                // 같은 room만 렌더링용 remotePlayers에 추가
                if (players[sid].room === comp.currentRoom) {
                    comp.remotePlayers[sid] = pData;
                }
            }
        }
        comp.updateOnlineCount();
        if (comp._socketTimeout) { clearTimeout(comp._socketTimeout); comp._socketTimeout = null; }
        comp.rebuildPlayerCaches();
        comp.setLoadingProgress(90, '렌더링 준비 중...');
        comp.startSyncInterval();
        comp.loadChatHistory();
    });

    // 새 플레이어 입장
    socket.on("player_joined", (data: any) => {
        if (data.sid === comp.mySid) return;
        const pData = {
            sid: data.sid,
            userId: data.userId || '',
            nickname: data.nickname,
            x: data.x,
            y: data.y,
            targetX: data.x,
            targetY: data.y,
            color: data.color,
            status: data.status || 'in',
            department: data.department || '',
            realName: data.realName || '',
            title: data.title || '',
            shape: data.shape || 'circle',
            room: data.room || '',
        };
        // 전체 접속자 목록에 추가
        comp.allRemotePlayers[data.sid] = pData;
        // 같은 room이면 렌더링용 remotePlayers에도 추가
        if (data.room === comp.currentRoom) {
            comp.remotePlayers[data.sid] = pData;
        }
        comp.updateOnlineCount();
        comp.rebuildPlayerCaches();

        const joinMsg: ChatMessage = {
            sid: 'system',
            nickname: '',
            message: `${data.nickname}님이 입장하였습니다.`,
            color: '#94A3B8',
            timestamp: Date.now(),
            type: 'system',
            noUnread: true,
        };
        comp.chatMessages = [...comp.chatMessages, joinMsg];
        comp.service.render();
    });

    // 다른 플레이어 이동
    socket.on("player_moved", (data: any) => {
        if (data.sid === comp.mySid) return;
        if (comp.remotePlayers[data.sid]) {
            comp.remotePlayers[data.sid].targetX = data.x;
            comp.remotePlayers[data.sid].targetY = data.y;
        }
    });

    // 플레이어 퇴장
    socket.on("player_left", (data: any) => {
        if (data.sid === comp.mySid) return;
        const leftNickname = comp.allRemotePlayers[data.sid]?.nickname || comp.remotePlayers[data.sid]?.nickname || '알 수 없는 사용자';
        delete comp.allRemotePlayers[data.sid];
        delete comp.remotePlayers[data.sid];
        comp.speechBubbles.delete(data.sid);
        comp.updateOnlineCount();
        comp.rebuildPlayerCaches();

        const leaveMsg: ChatMessage = {
            sid: 'system',
            nickname: '',
            message: `${leftNickname}님이 퇴장하였습니다.`,
            color: '#94A3B8',
            timestamp: Date.now(),
            type: 'system',
            noUnread: true,
        };
        comp.chatMessages = [...comp.chatMessages, leaveMsg];
        comp.service.render();
    });

    // 채팅 메시지 수신 (자기 자신의 메시지는 이미 로컬에서 처리됨)
    socket.on("chat_message", (data: any) => {
        if (data.sid === comp.mySid) return;

        const msg: ChatMessage = {
            sid: data.sid,
            userId: data.userId || '',
            nickname: data.nickname,
            message: data.message,
            color: data.color,
            timestamp: Date.now(),
            scope: data.scope || 'center',
        };
        comp.chatMessages = [...comp.chatMessages, msg];

        comp.speechBubbles.set(data.sid, {
            message: data.message,
            expireAt: Date.now() + comp.BUBBLE_DURATION,
            scope: data.scope || 'center',
        });
        comp.service.render();
    });

    // 전체 채팅 메시지 수신 (모든 room에 broadcast)
    socket.on("global_chat_message", (data: any) => {
        if (data.sid === comp.mySid) return;

        const msg: ChatMessage = {
            sid: data.sid,
            userId: data.userId || '',
            nickname: data.nickname,
            message: data.message,
            color: data.color,
            timestamp: Date.now(),
            scope: 'global',
        };
        comp.chatMessages = [...comp.chatMessages, msg];

        // 전체 채팅도 말풍선 표시
        comp.speechBubbles.set(data.sid, {
            message: data.message,
            expireAt: Date.now() + comp.BUBBLE_DURATION,
            scope: 'global',
        });
        comp.service.render();
    });

    // 상태 변경 수신
    socket.on("status_changed", (data: any) => {
        if (data.sid === comp.mySid) {
            comp.myStatus = data.status;
        } else {
            if (comp.remotePlayers[data.sid]) comp.remotePlayers[data.sid].status = data.status;
            if (comp.allRemotePlayers[data.sid]) comp.allRemotePlayers[data.sid].status = data.status;
        }
        comp.rebuildPlayerCaches();
        comp.service.render();
    });

    // 외형 변경 수신
    socket.on("appearance_changed", (data: any) => {
        if (data.sid === comp.mySid) {
            comp.myShape = data.shape || comp.myShape;
        } else {
            if (comp.remotePlayers[data.sid] && data.shape) comp.remotePlayers[data.sid].shape = data.shape;
            if (comp.allRemotePlayers[data.sid] && data.shape) comp.allRemotePlayers[data.sid].shape = data.shape;
        }
    });

    // 귓속말 수신
    socket.on("whisper_message", (data: any) => {
        const isSent = data.direction === 'sent';
        const msg: ChatMessage = {
            sid: data.from_sid,
            userId: isSent ? (data.from_userId || '') : (data.from_userId || ''),
            nickname: isSent ? `→ ${data.to_nickname}` : data.from_nickname,
            message: data.message,
            color: data.color || '#A78BFA',
            timestamp: Date.now(),
            type: 'whisper',
            noUnread: isSent,
        };
        if (isSent) {
            msg.userId = data.from_userId || '';
        }
        comp.chatMessages = [...comp.chatMessages, msg];
        comp.service.render();
    });

    // 찌르기 수신 (토스트 알림으로 표시)
    socket.on("poke_event", (data: any) => {
        comp.showPokeToast(`${data.from_nickname}님이 ${data.target_nickname}님을 찔렀어요!`);

        comp.pokeAnimations.set(data.target_sid, {
            startTime: Date.now(),
            fromX: data.from_sid === comp.mySid ? comp.playerX : (comp.remotePlayers[data.from_sid]?.x || 0),
            fromY: data.from_sid === comp.mySid ? comp.playerY : (comp.remotePlayers[data.from_sid]?.y || 0),
        });
        comp.service.render();
    });

    // 쪽지 수신 (토스트 알림)
    socket.on("note_received", (data: any) => {
        const senderUserId = data.from_userId || '';
        let senderPlayer: any = null;
        if (senderUserId) {
            for (const sid of Object.keys(comp.allRemotePlayers)) {
                const player = comp.allRemotePlayers[sid];
                if (player.userId === senderUserId) {
                    senderPlayer = player;
                    break;
                }
            }
        }
        const senderNickname = senderPlayer?.nickname || data.from_nickname || '익명';
        const senderColor = senderPlayer?.color || '#4F8EF7';
        const note = {
            from: senderNickname,
            nickname: senderNickname,
            fromUserId: senderUserId,
            userId: senderUserId,
            message: data.message,
            time: new Date(),
            read: false,
            color: senderColor,
        };
        comp.receivedNotes.unshift(note);

        // 토스트 알림 표시
        comp.noteToast = { from: senderNickname };
        if (comp.noteToastTimeout) clearTimeout(comp.noteToastTimeout);
        comp.noteToastTimeout = setTimeout(() => {
            comp.noteToast = null;
            comp.service.render();
        }, 4000);

        // 사이드메뉴: 즉시 반영 (optimistic update)
        if (comp.sidemenuRef) {
            comp.sidemenuRef.unreadDbNoteCount = (comp.sidemenuRef.unreadDbNoteCount || 0) + 1;
            comp.sidemenuRef.dbNotes.unshift({
                id: 'temp_' + Date.now(),
                sender_id: senderUserId,
                sender_nickname: senderNickname,
                message: data.message,
                created: new Date().toISOString(),
                is_read: false,
            });
            comp.sidemenuRef.triggerBadgeShake();
        }

        comp.service.render();

        // 백그라운드 DB 동기화 (실제 데이터로 교체)
        setTimeout(() => {
            if (comp.sidemenuRef?.loadDbNotes) {
                comp.sidemenuRef.loadDbNotes();
            }
        }, 1000);
    });

    // 쪽지 전송 확인
    socket.on("note_sent", (_data: any) => { });

    // 색상 변경 수신
    socket.on("color_changed", (data: any) => {
        if (data.sid === comp.mySid) {
            comp.playerColor = data.color;
        } else {
            if (comp.remotePlayers[data.sid]) comp.remotePlayers[data.sid].color = data.color;
            if (comp.allRemotePlayers[data.sid]) comp.allRemotePlayers[data.sid].color = data.color;
        }
    });

    // 닉네임 변경 수신
    socket.on("nickname_changed", (data: any) => {
        if (data.sid === comp.mySid) {
            comp.nickname = data.nickname;
            localStorage.setItem('space_nickname', data.nickname);
        } else {
            if (comp.remotePlayers[data.sid]) comp.remotePlayers[data.sid].nickname = data.nickname;
            if (comp.allRemotePlayers[data.sid]) comp.allRemotePlayers[data.sid].nickname = data.nickname;
        }
        comp.rebuildPlayerCaches();
        comp.service.render();
    });

    // 입력 중 표시 수신 (자기 자신 포함)
    socket.on("typing_start", (data: any) => {
        if (data.sid === comp.mySid) return;
        comp.typingPlayers[data.sid] = data.nickname;
        if (comp.typingTimeouts[data.sid]) clearTimeout(comp.typingTimeouts[data.sid]);
        comp.typingTimeouts[data.sid] = setTimeout(() => {
            delete comp.typingPlayers[data.sid];
            delete comp.typingTimeouts[data.sid];
            comp.service.render();
        }, 3000);
        comp.service.render();
    });

    socket.on("typing_stop", (data: any) => {
        if (data.sid === comp.mySid) return;
        delete comp.typingPlayers[data.sid];
        if (comp.typingTimeouts[data.sid]) {
            clearTimeout(comp.typingTimeouts[data.sid]);
            delete comp.typingTimeouts[data.sid];
        }
        comp.service.render();
    });

    // 점프 수신
    socket.on("player_jump", (data: any) => {
        if (data.sid === comp.mySid) return;
        const player = comp.remotePlayers[data.sid];
        if (player) {
            player.isJumping = true;
            player.jumpStartTime = Date.now();
        }
    });

    // 회의실 웨비나 시그널링
    socket.on("webinar_joined", (data: any) => {
        comp.onWebinarJoined(data);
    });

    socket.on("webinar_join_denied", (data: any) => {
        comp.onWebinarJoinDenied(data);
    });

    socket.on("webinar_participant_joined", (data: any) => {
        comp.onWebinarParticipantJoined(data);
    });

    socket.on("webinar_participants", (data: any) => {
        comp.onWebinarParticipants(data);
    });

    socket.on("webinar_participant_left", (data: any) => {
        comp.onWebinarParticipantLeft(data);
    });

    socket.on("webinar_media_state", (data: any) => {
        comp.onWebinarMediaState(data);
    });

    socket.on("webinar_force_leave", (data: any) => {
        comp.onWebinarForceLeave(data);
    });

    socket.on("webinar_signal", (data: any) => {
        comp.onWebinarSignal(data);
    });

    socket.on("disconnect", () => {
        comp.leaveWebinar?.('disconnect', false);
        comp.remotePlayers = {};
        comp.allRemotePlayers = {};
        comp.updateOnlineCount();
    });

}
